'use memo'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  hasPlaceholderTeam,
  pickSubCompetitionValue,
  toGameFormValues,
} from '@sportninja/common/features/game/gameForm'
import type {
  GameEntityLike,
  GameFormMode,
  GameFormValues,
} from '@sportninja/common/features/game/gameForm'
import { getBrowserTimezone } from '@sportninja/common/ts-utils/timezone-display'
import { rebaseGameZone } from '@sportninja/common/features/game/gameForm/helpers/rebaseGameZone'

interface UseGameFormOptions {
  mode: GameFormMode
  game?: GameEntityLike | null
  /** Reseeds the form when the modal opens. */
  isOpen: boolean
  /**
   * Team options, needed to tell whether a selection is a PLACEHOLDER --
   * `is_placeholder` lives on the option, not on the form value.
   */
  teams?: Array<{ id: string; is_placeholder?: boolean | null }>
  /**
   * Venue options, needed for their `timezone`. Selecting a venue has to write
   * `locationTimezone` into the form: the create path re-zones the picked wall
   * clock into the venue's zone before posting, and without this that re-zone
   * silently no-ops and the game saves at the browser's local time.
   */
  venues?: Array<{ id: string; timezone?: string | null }>
  /**
   * The competition's divisions. Used to resolve `scheduleId`, which is seeded
   * from a value that is not always one of them.
   */
  subCompetitionOptions?: Array<{ value: string }>
  /**
   * Whether the division list has ANSWERED. Not the inverse of a loading flag:
   * the query is idle until a placeholder team is selected, and an idle query
   * is neither loading nor answered.
   */
  subCompetitionsResolved?: boolean
}

/**
 * Form state for the add/edit Game modal.
 *
 * Validation is almost entirely the API's: its `invalid_fields` response is
 * translated into `fieldErrors` by `applyGameApiError` -- the same flow the
 * family and player forms use. The one exception is Duration, checked before
 * submit by `validateGameForm` because an empty value has no server error to
 * surface. Both write the same `fieldErrors` map, so they render identically
 * and a re-edit of the field clears either one.
 */
export const useGameForm = ({
  mode,
  game,
  isOpen,
  teams = [],
  venues = [],
  subCompetitionOptions = [],
  subCompetitionsResolved = false,
}: UseGameFormOptions) => {
  const seed = useMemo(() => toGameFormValues(game, mode), [game, mode])

  /*
   * Held in a ref, not closed over: `change` is a useCallback with no deps, so
   * capturing `venues` directly would pin the empty first-render array and the
   * timezone lookup would always miss.
   */
  const venuesRef = useRef(venues)
  venuesRef.current = venues

  const [values, setValues] = useState<GameFormValues>(seed)
  const [hasChanged, setHasChanged] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Reseed on open so a cancelled edit does not persist into the next one.
  useEffect(() => {
    if (isOpen) {
      setValues(seed)
      setHasChanged(false)
      setFieldErrors({})
    }
  }, [isOpen, seed])

  const slots = useMemo(
    () => ({
      homeTeamSlotId: game?.homeTeamSlot?.id ?? null,
      visitingTeamSlotId: game?.visitingTeamSlot?.id ?? null,
    }),
    [game]
  )

  const placeholderTeam = hasPlaceholderTeam(values, teams, game)

  /*
   * Drop a division chosen for a placeholder once neither side is one.
   *
   * The field hides at that point, so a value left behind is invisible AND
   * still posted — the legacy GameScheduleSelect cleared `schedule_id` for
   * exactly this reason. Guarded on the teams list being loaded: before it
   * arrives nothing can be known to be a placeholder, and clearing then would
   * wipe the division seeded from the game being edited.
   */
  useEffect(() => {
    if (teams.length > 0 && !placeholderTeam && values.scheduleId) {
      setValues((prev) => ({ ...prev, scheduleId: null }))
    }
  }, [placeholderTeam, teams.length, values.scheduleId])

  /*
   * Resolve the division once its options are known.
   *
   * The seeded `scheduleId` is the competition the game is filed under, which
   * is only sometimes a division -- a game filed on the season carries the
   * SEASON's id, which no option has. `pickSubCompetitionValue` keeps a real
   * match and otherwise falls to the first division.
   *
   * Written into form state rather than resolved at render: the fallback is a
   * choice made for the admin, and a value merely displayed would still submit
   * the season id the field appears to have replaced.
   *
   * Runs only while a placeholder team is selected -- that is the sole case
   * where the field is visible and `schedule_id` is required. `change` is
   * deliberately not used: this is a correction, not an edit, so it must not
   * set `hasChanged` and make an untouched form look dirty.
   *
   * Gated on RESOLVED, not on `!isLoading`. The divisions query is idle until
   * the placeholder flag arrives, and an idle react-query reports
   * `isLoading: false` with no data -- indistinguishable from "answered, and
   * this competition has none". Resolving in that window discarded a seeded
   * division that was genuinely in the list and then fell to the first option,
   * silently moving the game to a different division.
   */
  useEffect(() => {
    if (!placeholderTeam || !subCompetitionsResolved) {
      return
    }
    const resolved = pickSubCompetitionValue(
      values.scheduleId,
      subCompetitionOptions
    )
    if (resolved !== values.scheduleId) {
      setValues((prev) => ({ ...prev, scheduleId: resolved }))
    }
  }, [
    placeholderTeam,
    subCompetitionOptions,
    subCompetitionsResolved,
    values.scheduleId,
  ])

  const change = useCallback(
    (next: Partial<GameFormValues>) => {
      setHasChanged(true)
      setValues((prev) => {
        const merged = { ...prev, ...next }
        if ('venueId' in next && next.venueId !== prev.venueId) {
          // Choosing a different location invalidates the facility under it.
          merged.facilityId = null

          /*
           * ...and carries its own timezone, which the create path re-zones the
           * start time into.
           *
           * Deliberately writes null when the new venue has no timezone, or when
           * the venue is cleared. Legacy only wrote the value when truthy, which
           * meant switching to a venue without a zone silently kept the PREVIOUS
           * venue's -- re-zoning the start into a timezone that belongs to a
           * venue no longer selected.
           */
          merged.locationTimezone =
            venuesRef.current.find((venue) => venue.id === next.venueId)
              ?.timezone ?? null

          /*
           * ...and the start moves with it, holding the CLOCK rather than the
           * instant. The admin picked "9:15 PM at this venue", so sending the
           * game to a venue in another zone keeps 9:15 PM and lands on a
           * different moment.
           *
           * Edit only. Create needs no rebase: it re-zones the typed clock at
           * submit through whichever venue is selected by then, so it already
           * behaves this way. Doing it here as well would apply the shift twice.
           *
           * The fallback on either side is the viewer's own zone, matching what
           * the picker displays and what the publish path does for a game with
           * no location timezone.
           */
          if (mode === 'edit' && merged.startsAt) {
            const browserZone = getBrowserTimezone()
            merged.startsAt = rebaseGameZone(
              merged.startsAt,
              prev.locationTimezone || browserZone,
              merged.locationTimezone || browserZone
            )
          }
        }
        return merged
      })
      // Clear the server-side error on any field the user just edited.
      setFieldErrors((prev) => {
        const keys = Object.keys(next)
        if (keys.every((k) => !(k in prev))) {
          return prev
        }
        const cleared = { ...prev }
        keys.forEach((k) => delete cleared[k])
        return cleared
      })
      //  `mode` is read by the venue rebase above. It does not change for the
      //  life of a modal, so this stays as stable as the empty list it replaces.
    },
    [mode]
  )

  const hasError = useCallback(
    (name: keyof GameFormValues) => Boolean(fieldErrors[name as string]),
    [fieldErrors]
  )

  return {
    values,
    change,
    hasError,
    hasChanged,
    hasPlaceholderTeam: placeholderTeam,
    slots,
    fieldErrors,
    setFieldErrors,
  }
}
